import { useState, useRef } from "react";
import { Camera, Loader2, Sparkles, AlertTriangle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Resize + convert an image File to a base64 data URL <= ~1MB
async function fileToBase64(file, maxDim = 1400, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ScanVaccinationCard({ profiles, onImported }) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | analyzing | done | error
  const [entries, setEntries] = useState([]);
  const [warning, setWarning] = useState(null);
  const [error, setError] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [imported, setImported] = useState(0);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setEntries([]);
    setWarning(null);
    setImported(0);
    setStatus("analyzing");
    setSelectedProfileId(profiles?.[0]?.id || "");
    try {
      const dataUrl = await fileToBase64(file);
      setPreview(dataUrl);
      setOpen(true);
      const resp = await fetch(`${BACKEND}/api/ai/analyze-document`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ doc_type: "vaccination", image_base64: dataUrl }),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.detail || `AI service returned ${resp.status}`);
      }
      const data = await resp.json();
      setEntries(data.entries || []);
      setWarning(data.warning || null);
      setStatus("done");
    } catch (err) {
      setError(err.message || "Could not analyze the image.");
      setStatus("error");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const toggleEntry = (i, key, val) => {
    setEntries((list) => list.map((e, idx) => (idx === i ? { ...e, [key]: val } : e)));
  };

  const importAll = async () => {
    if (!selectedProfileId) {
      setError("Pick a profile to import into.");
      return;
    }
    setError("");
    const profile = profiles.find((p) => p.id === selectedProfileId);
    const { data: userData } = await supabase.auth.getUser();
    const user_id = userData?.user?.id;
    if (!user_id) {
      setError("You need to be signed in.");
      return;
    }
    const selected = entries.filter((e) => e.__selected !== false && e.vaccine_name && e.date_administered);
    if (selected.length === 0) {
      setError("Nothing to import — every row needs at least vaccine + date.");
      return;
    }
    const rows = selected.map((e) => ({
      user_id,
      profile_id: selectedProfileId,
      profile_name: profile?.name || null,
      vaccine_name: e.vaccine_name,
      date_administered: e.date_administered,
      dose_number: e.dose_number ?? null,
      provider: e.provider || null,
      lot_number: e.lot_number || null,
      date_due: e.date_due || null,
      status: "completed",
      notes: e.notes || null,
    }));
    const { error: insErr } = await supabase.from("vaccinations").insert(rows);
    if (insErr) {
      setError(insErr.message);
      return;
    }
    setImported(rows.length);
    onImported?.();
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
        data-testid="vacc-scan-input"
      />
      <Button
        variant="outline"
        onClick={onPick}
        disabled={!profiles || profiles.length === 0}
        className="bg-accent/10 border-accent/30 text-accent-foreground hover:bg-accent/20"
        data-testid="vacc-scan-btn"
      >
        <Sparkles className="mr-1 h-4 w-4" />
        Scan card
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="vacc-scan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-accent" />
              Scan vaccination card
            </DialogTitle>
          </DialogHeader>

          {imported > 0 ? (
            <div className="flex flex-col items-center py-8 text-center" data-testid="vacc-scan-success">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100">
                <Check className="h-7 w-7 text-emerald-700" />
              </div>
              <h3 className="mt-4 font-heading text-xl font-bold">
                Imported {imported} vaccination{imported === 1 ? "" : "s"}
              </h3>
              <Button className="mt-6" onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
          ) : (
            <>
              {preview && (
                <img
                  src={preview}
                  alt="Card preview"
                  className="mx-auto max-h-56 rounded-xl border border-border object-contain"
                />
              )}

              {status === "analyzing" && (
                <div className="flex items-center justify-center gap-3 py-8 text-muted-foreground" data-testid="vacc-scan-loading">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>gpt-5.2 is reading the card…</span>
                </div>
              )}

              {status === "error" && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="vacc-scan-error">
                  <AlertTriangle className="mr-1 inline h-4 w-4" />
                  {error}
                </div>
              )}

              {status === "done" && (
                <div className="space-y-4">
                  {warning && (
                    <div className="rounded-lg bg-amber-100 px-3 py-2 text-sm text-amber-800">
                      Heads up — the model wasn't fully confident this is a vaccination card. Review carefully.
                    </div>
                  )}
                  {entries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No vaccination entries could be extracted. Try a clearer photo, or add them manually.
                    </p>
                  ) : (
                    <>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Import into profile
                        </label>
                        <select
                          value={selectedProfileId}
                          onChange={(e) => setSelectedProfileId(e.target.value)}
                          className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          data-testid="vacc-scan-profile"
                        >
                          {profiles.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {entries.map((e, i) => (
                          <div key={i} className="rounded-xl border border-border p-3" data-testid={`vacc-scan-entry-${i}`}>
                            <label className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                defaultChecked
                                onChange={(evt) => toggleEntry(i, "__selected", evt.target.checked)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="font-heading text-sm font-bold">
                                  {e.vaccine_name || "(unnamed vaccine)"}
                                </div>
                                <div className="mt-0.5 text-xs text-muted-foreground">
                                  {e.date_administered || "no date"}
                                  {e.dose_number ? ` · dose ${e.dose_number}` : ""}
                                  {e.provider ? ` · ${e.provider}` : ""}
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>

                      {error && (
                        <div className="text-xs text-destructive">{error}</div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {status === "done" && entries.length > 0 && imported === 0 && (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={importAll} data-testid="vacc-scan-import">
                Import selected
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
